import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  useGetWorkingHoursQuery,
  useGetAllProjectsForMatrixQuery,
} from "../../../../store/appApi";
import { selectUserRole } from "../../../../store/authSlice";
import PageTitle from "../../../layouts/PageTitle";
import { downloadAttendanceRegister } from "../../../../services/attendance";

const ymd = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(
    x.getDate(),
  ).padStart(2, "0")}`;
};

const monthStart = () => {
  const d = new Date();
  return ymd(new Date(d.getFullYear(), d.getMonth(), 1));
};

const TAG_SHORT = {
  PRESENT: "",
  PARTIAL: "P",
  HALF_DAY_PRESENT: "½",
  HALF_DAY_LEAVE: "HL",
  ON_LEAVE: "L",
  ON_LEAVE_FIRST_HALF: "L",
  ON_LEAVE_SECOND_HALF: "L",
  PENDING: "·",
  ABSENT: "A",
  HOLIDAY: "H",
  UPCOMING: "",
};
const TAG_COLOR = {
  ABSENT: "#f32d2d",
  HOLIDAY: "#8b5cf6",
  HALF_DAY_LEAVE: "#3f9ae0",
  ON_LEAVE: "#14b8a6",
  ON_LEAVE_FIRST_HALF: "#14b8a6",
  ON_LEAVE_SECOND_HALF: "#14b8a6",
  PARTIAL: "#f6a821",
  PENDING: "#ff9f43",
};

const WorkingHoursSheet = () => {
  const role = useSelector(selectUserRole);
  const isAdmin = role === "super-admin" || role === "Project Manager";

  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(ymd(new Date()));
  const [projectId, setProjectId] = useState("");
  const [q, setQ] = useState("");

  const { data, isLoading, isFetching } = useGetWorkingHoursQuery(
    { from, to, projectId: projectId || undefined },
    { skip: !isAdmin },
  );
  const { data: projectsData } = useGetAllProjectsForMatrixQuery(undefined, {
    skip: !isAdmin,
  });
  const projects = projectsData?.data || [];

  const dates = data?.dates || [];
  const rows = useMemo(
    () =>
      (data?.rows || []).filter((r) =>
        r.name?.toLowerCase().includes(q.trim().toLowerCase()),
      ),
    [data, q],
  );

  // Ticked employees. Empty = nobody ticked → downloads include everyone shown.
  const [selected, setSelected] = useState(() => new Set());
  const allShownSelected =
    rows.length > 0 && rows.every((r) => selected.has(String(r.employeeId)));
  const toggleOne = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(String(id))) n.delete(String(id));
      else n.add(String(id));
      return n;
    });
  const toggleAll = () =>
    setSelected((prev) => {
      const n = new Set(prev);
      rows.forEach((r) =>
        allShownSelected ? n.delete(String(r.employeeId)) : n.add(String(r.employeeId)),
      );
      return n;
    });
  const chosenRows = selected.size
    ? rows.filter((r) => selected.has(String(r.employeeId)))
    : rows;

  const [regBusy, setRegBusy] = useState(false);
  const exportRegister = async () => {
    try {
      setRegBusy(true);
      await downloadAttendanceRegister({
        from,
        to,
        projectId: projectId || undefined,
        employeeIds: chosenRows.map((r) => String(r.employeeId)),
      });
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Could not download the register.");
    } finally {
      setRegBusy(false);
    }
  };

  const exportCsv = () => {
    const head = ["Employee", "Role", ...dates, "Total Hours"].join(",");
    const body = chosenRows
      .map((r) =>
        [
          `"${r.name}"`,
          `"${r.role || ""}"`,
          ...r.days.map((d) => (d.minutes ? (d.minutes / 60).toFixed(2) : "")),
          r.totalHours.toFixed(2),
        ].join(","),
      )
      .join("\n");
    const blob = new Blob([`${head}\n${body}`], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `working-hours_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!isAdmin) {
    return (
      <>
        <PageTitle activeMenu="Working Hours" motherMenu="Attendance" />
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            This sheet is available to admins only.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Working Hours" motherMenu="Attendance" />
      <div className="card">
        <div className="card-header d-flex flex-wrap gap-2 align-items-end">
          <h4 className="card-title mb-0 me-auto">
            Working Hours {isFetching ? "…" : ""}
          </h4>
          <div>
            <label className="form-label mb-0 small">From</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label mb-0 small">To</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={to}
              min={from}
              max={ymd(new Date())}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <select
            className="form-select form-select-sm"
            style={{ width: 180 }}
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>
                {p.projectName}
              </option>
            ))}
          </select>
          <input
            className="form-control form-control-sm"
            style={{ width: 160 }}
            placeholder="Search employee…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button
            className="btn btn-sm btn-success"
            disabled={regBusy}
            onClick={exportRegister}
          >
            {regBusy
              ? "Preparing…"
              : selected.size
                ? `Download Register (${chosenRows.length} selected)`
                : "Download Register (Excel)"}
          </button>
          {selected.size > 0 && (
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setSelected(new Set())}
            >
              Clear selection
            </button>
          )}
          <button className="btn btn-sm btn-outline-primary" onClick={exportCsv}>
            Export CSV
          </button>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-4">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-center text-muted py-4">No data.</div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: "70vh" }}>
              <table
                className="table table-sm table-bordered align-middle"
                style={{ fontSize: 12 }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        position: "sticky",
                        left: 0,
                        background: "#f8f9fa",
                        zIndex: 2,
                        minWidth: 150,
                      }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={allShownSelected}
                        onChange={toggleAll}
                        title="Select all shown"
                      />
                      Employee
                    </th>
                    {dates.map((d) => (
                      <th
                        key={d}
                        className="text-center"
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {d.slice(8)}/{d.slice(5, 7)}
                      </th>
                    ))}
                    <th className="text-center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.employeeId}>
                      <td
                        style={{
                          position: "sticky",
                          left: 0,
                          background: "#fff",
                          zIndex: 1,
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={selected.has(String(r.employeeId))}
                          onChange={() => toggleOne(r.employeeId)}
                        />
                        {r.name}
                        <div className="text-muted" style={{ fontWeight: 400, paddingLeft: 24 }}>
                          {r.role}
                        </div>
                      </td>
                      {r.days.map((d) => {
                        const hrs = d.minutes
                          ? (d.minutes / 60).toFixed(1)
                          : "";
                        const badge = TAG_SHORT[d.tag] ?? "";
                        return (
                          <td
                            key={d.date}
                            className="text-center"
                            style={{
                              color: TAG_COLOR[d.tag] || "#2b2b40",
                              fontWeight: hrs ? 600 : 400,
                              whiteSpace: "nowrap",
                            }}
                            title={`${d.date} · ${d.holiday ? `Holiday — ${d.holiday}` : d.tag}${
                              d.punchIn
                                ? ` · in ${new Date(
                                    d.punchIn,
                                  ).toLocaleTimeString()}`
                                : ""
                            }${
                              d.punchOut
                                ? ` · out ${new Date(
                                    d.punchOut,
                                  ).toLocaleTimeString()}`
                                : ""
                            }`}
                          >
                            {hrs || badge || "—"}
                          </td>
                        );
                      })}
                      <td
                        className="text-center"
                        style={{ fontWeight: 700, background: "#f8f9fa" }}
                      >
                        {r.totalHours.toFixed(1)}h
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-muted small mt-2">
            Cells show worked hours. Codes — <strong>A</strong> absent,{" "}
            <strong>HL</strong> half-day leave, <strong>L</strong> on leave,{" "}
            <strong>P</strong> partial, <strong>½</strong> first-half present,{" "}
            <strong>H</strong> declared holiday.
            Punch-out is capped at 6:30 PM.
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkingHoursSheet;
